import React, { useImperativeHandle, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Divider, Flex, Form, Input, Layout, Modal, Popconfirm, Radio, Space, Tabs, Tag, Tooltip, Typography, Upload } from 'antd';
import { CloseOutlined, DeleteOutlined, PlusOutlined, QuestionCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { Routes } from '../../../routes';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import Sidebar from '../GenericCreateEdit/SidebarActions';
import GenericTab, { TAB_STATES } from '../GenericCreateEdit/GenericTab';
import AudienceTab, { formatSegmentationForm } from '../GenericCreateEdit/AudienceTab';
import { FeaturedFormItem, ThumbnailFormItem } from '../GenericCreateEdit/GenericFormItems';
import { ColoredTag } from '../GenericCreateEdit/GenericElements';
import { useCreateEditGeneric } from '../GenericCreateEdit/useCreateEditGeneric';
import ExpandableAlert from '../../../components/errors/ExpandableAlert';
import { DEFAULT_LOCALE_CODE } from '../../../const';
import { LanguageSelectorCMX } from '../../../components/formItems';
import { useFeatureFlag } from '../../../featureFlags';
import { flags } from '../../../featureFlags/flags';
import { isValidURL } from '../../utils';
import classNames from 'classnames';
import {
    RESOURCE_TYPE,
    useCreateResourceMutation,
    useDeleteResourceMutation,
    usePatchResourceMutation,
    useResourceLocalizationMutations,
    useResourceQuery
} from '../../gql/hooks/useResourceQueries';
import { useWatch } from 'antd/es/form/Form';
import { TagsTab } from '../GenericCreateEdit/TagsTab';
import { useTimedUserInput } from '../../hooks/useTimedUserInput';

function formatResourceForm( r ) {
    return {
        id: r.id,
        type: r.type,
        isPublished: r.isPublished,
        isFeatured: r.isFeatured,
        image: r.image,
        localizations: r.localizations,
        tagIds: r.tagIds ?? [],
        ...formatSegmentationForm( r ),
    };
}

function formatResourceInput( r ) {
    // defaultLocalization only exists while creating, localizations only while editing
    const defaultLocalization = r.defaultLocalization ?? r.localizations?.find( l => l.isDefault );

    return {
        resourceId: r.id,
        // the resource keeps a copy of the default localization's name and description
        name: defaultLocalization?.name,
        description: defaultLocalization?.description,
        type: r.type,
        image: r.image,
        isPublished: r.isPublished,
        isFeatured: r.isFeatured,
        defaultLocalization: r.defaultLocalization,
    };
}

const creationInitialValues = {
    type: RESOURCE_TYPE.FILE,
    isPublished: false,
    isFeatured: false,
    image: null,
    defaultLocalization: { locale: DEFAULT_LOCALE_CODE },
};

const creationTabStates = {
    resource: [ TAB_STATES.UNSAVED ],
    localizations: [ TAB_STATES.REQUIRED ],
    audience: [ TAB_STATES.LOCKED ],
    tags: [ TAB_STATES.LOCKED ],
};

const CreateEditResourcePage = ( { route: { params: { resourceId } } } ) => {

    const resourceRef = useRef();
    const audienceRef = useRef();
    const locsRef = useRef();
    const tagRef = useRef();

    const [ fetchResource ] = useResourceQuery();
    const [ createResourceMutation ] = useCreateResourceMutation();
    const [ patchResourceMutation ] = usePatchResourceMutation();
    const [ deleteResourceMutation ] = useDeleteResourceMutation();

    // `id` comes from the hook rather than the route param, which is still undefined on the first
    // save after a create
    async function beforeSave( { form, input, id } ) {
        const data = { ...input };

        // uploads the thumbnail only if the cropper left a data url behind. Guarded on the ref
        // rather than merged, so a never-visited resource tab leaves the existing image alone
        if ( resourceRef.current ) {
            data.image = await resourceRef.current.onSubmit();
        }

        // tags and segmentation are not part of PatchResourceInput, and both tabs are locked
        // while creating - the resource has to exist before anything can be attached to it
        if ( id ) {
            await audienceRef.current?.onSubmit( { objectType: 'resource', objectId: id } );
            await tagRef.current?.onSubmit( { objectType: 'resource', objectId: id } );
        }

        return data;
    }

    const {
        isCreating, loading, error, setActiveTab, activeTab, isPublished,
        form,
        itemId,
        getTabIcon, tabStates,
        onValidateTab,
        softUpdateGeneric,
        messageContext,
        sidebarProps, tabProps,
    } = useCreateEditGeneric( {
        id: resourceId, idName: 'resourceId',
        label: 'resource',
        beforeSave,
        editRoute: Routes.EditResource, viewRoute: Routes.ResourcesPage,
        creationTabStates, creationInitialValues,
        createMutation: createResourceMutation,
        fetch: fetchResource,
        patchMutation: patchResourceMutation,
        deleteMutation: deleteResourceMutation,
        formatDataToForm: formatResourceForm, formatFormToData: formatResourceInput
    } );

    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Learning Content', path: Routes.LearningContentNav.buildPath() },
        { name: 'Resources', path: Routes.ResourcesPage.buildPath() },
        { name: isCreating ? 'Create' : 'Edit' }
    ];

    const tabItems = useMemo( () => [
        {
            key: 'resource',
            label: 'Resource',
            icon: getTabIcon( tabStates.resource ),
            children: <Space className='body'>
                <ResourceTab { ...tabProps } ref={ resourceRef } isCreating={ isCreating } statuses={ tabStates.resource }
                    onValidateCb={ statuses => onValidateTab( 'resource', statuses ) } />
                <Sidebar tab='resource' tabFields={ resourceRef?.current?.formFields } { ...sidebarProps } />
            </Space>
        },
        {
            key: 'localizations',
            label: 'Localizations',
            icon: getTabIcon( tabStates.localizations ),
            children: <Space className='body'>
                <LocalizationsTab { ...tabProps } ref={ locsRef } isCreating={ isCreating } resourceId={ itemId } statuses={ tabStates.localizations }
                    onLocalizationsChange={ localizations => softUpdateGeneric( { localizations } ) }
                    onValidateCb={ statuses => onValidateTab( 'localizations', statuses ) } />
                <Sidebar { ...sidebarProps }
                    saveTooltip={ sidebarProps.saveTooltip ?? ( isCreating ? undefined : 'Changes to localizations are always saved automatically' ) } />
            </Space>
        },
        {
            key: 'audience',
            label: 'Audience',
            icon: getTabIcon( tabStates.audience ),
            disabled: isCreating,
            children: <Space className='body'>
                <AudienceTab { ...tabProps } ref={ audienceRef }
                    statuses={ tabStates.audience }
                    onValidateCb={ statuses => onValidateTab( 'audience', statuses ) } />
                <Sidebar tab='audience' { ...sidebarProps } />
            </Space>
        },
        {
            key: 'tags',
            label: 'Tags',
            icon: getTabIcon( tabStates.tags ),
            disabled: isCreating,
            children: <Space className='body'>
                <TagsTab { ...tabProps } ref={ tagRef }
                    statuses={ tabStates.tags }
                    onValidateCb={ statuses => onValidateTab( 'tags', statuses ) } />
                <Sidebar { ...sidebarProps } />
            </Space>
        },
    ], [ tabProps, isCreating, tabStates, sidebarProps, itemId ] );

    return (
        <Layout className="resource-edit-page">

            { messageContext }

            <Breadcrumbs crumbs={ breadcrumbs } />

            <Card
                className="resource-edit-content"
                title={ isCreating ? 'Create Resource' : 'Edit Resource' }
            >
                { error && error }

                <Form form={ form } layout='vertical' >

                    <Tabs
                        type="card"
                        size="large"
                        activeKey={ activeTab }
                        onChange={ setActiveTab }
                        items={ tabItems }
                        tabBarExtraContent={ {
                            right: loading ? <></> : <ColoredTag.Publish isPublished={ isPublished } />
                        } }
                    />
                </Form>

            </Card>

        </Layout>
    );
};

const ResourceTab = ( { ref, form, isCreating, statuses, parentError, loading, onValidateCb, disabled } ) => {
    const formNames = [ 'type', 'isFeatured', 'image' ];

    const thumbnailRef = useRef();

    const type = useWatch( 'type' );

    const urlAllowed = useFeatureFlag( { name: flags.URL_RESOURCE_ENABLED } );

    useImperativeHandle( ref, () => ( {
        formFields: formNames,
        onSubmit: async () => await thumbnailRef.current?.onSubmit(),
    } ) );

    return <GenericTab loading={ loading } parentError={ parentError } statuses={ statuses } title='Resource Information' onValidateCb={ onValidateCb } form={ form } formNames={ formNames }>
        { ( { validateTab, makeDirty } ) => (
            <Flex vertical={ false } wrap>
                <ThumbnailFormItem
                    ref={ thumbnailRef }
                    form={ form }
                    title='Thumbnail'
                    extra={ <Tooltip title='Image shown with the resource in lists and cards.' ><QuestionCircleOutlined /></Tooltip> }
                    disabled={ disabled }
                    onDirty={ makeDirty }
                    onValidate={ validateTab }
                />

                <FeaturedFormItem
                    disabled={ disabled }
                    onDirty={ makeDirty }
                />

                { urlAllowed &&
                    <Card
                        title='Type'
                        className={ classNames( { 'card-required': isCreating } ) }
                        style={ { maxWidth: 300 } }
                        extra={ type === 'file' ? <ColoredTag.Icon value='File' /> : <ColoredTag.Icon value='URL' /> }
                    >
                        <Form.Item name='type' style={ { margin: 0, justifySelf: 'center' } }>
                            <Radio.Group
                                optionType='button'
                                disabled={ disabled || !isCreating }
                                onChange={ () => validateTab() }
                                options={ [ { value: RESOURCE_TYPE.FILE, label: 'File' }, { value: RESOURCE_TYPE.URL, label: 'URL' } ] } />
                        </Form.Item>
                            
                        <Divider />

                        <p className='tip'>Resources can be either files or URLs. This can not be changed once the resource is created</p>
                    </Card>
                }

            </Flex>
        ) }
    </GenericTab>;
};

const LocalizationsTab = ( { form, item, isCreating, resourceId, statuses, parentError, loading, onValidateCb, onLocalizationsChange, disabled } ) => {
    // while creating, the default localization is authored inline and saved along with the
    // resource. once the resource exists each localization saves itself through its own modal.
    const formNames = isCreating ? [ 'defaultLocalization' ] : [ 'localizations' ];

    const type = Form.useWatch( 'type', form ) ?? item?.type ?? RESOURCE_TYPE.FILE;

    const genericTab = useRef();

    // revalidate when type changes (but not on init)
    const skipInit = useRef( true );
    useEffect( () => {
        if ( skipInit.current && !loading ) { skipInit.current = false; return; }
        genericTab.current?.validate?.();
    }, [ type, loading ] ); 

    return <GenericTab ref={ genericTab } loading={ loading } parentError={ parentError } statuses={ statuses } title='Localizations' onValidateCb={ onValidateCb } form={ form } formNames={ formNames }>
        { ( { validateTab, makeDirty } ) => ( isCreating ?
            <Card
                style={ { maxWidth: 600 } }
                title={ <><Tag color='green' >Default Localization</Tag><Tag color='blue'>{ DEFAULT_LOCALE_CODE }</Tag></> }
                extra={ <Tooltip title={ `The ${ DEFAULT_LOCALE_CODE } localization every learner falls back to. More localizations can be added once the resource is created.` } ><QuestionCircleOutlined /></Tooltip> }
            >
                <LocalizationFields namePrefix={ [ 'defaultLocalization' ] } type={ type } lockLocale disabled={ disabled }
                    validateCb={ () => validateTab() } dirtyCb={ () => makeDirty() } />
            </Card> :
            <Form.Item name='localizations'>
                <LocalizationsFormItem resourceId={ resourceId } type={ type } disabled={ disabled } onChange={ onLocalizationsChange } />
            </Form.Item>
        ) }
    </GenericTab>;
};

const LocalizationsFormItem = ( { value, onChange, resourceId, type, disabled } ) => {

    const [ fetchResource ] = useResourceQuery();

    // every save refetches the whole resource, so the order the server hands localizations back
    // in is not something to rely on. Remember the order they were last shown in instead.
    const shownOrder = useRef( [] );

    const localizations = useMemo( () => {
        const previous = shownOrder.current;
        const position = l => {
            const index = previous.indexOf( l.id );
            // anything that was not on screen before was just added, so it falls to the end
            return index === -1 ? previous.length : index;
        };

        const sorted = [ ...( value ?? [] ) ].sort( ( a, b ) => {
            if ( a.isDefault !== b.isDefault ) {
                return a.isDefault ? -1 : 1;
            }
            return position( a ) - position( b );
        } );

        shownOrder.current = sorted.map( l => l.id );
        return sorted;
    }, [ value ] );

    const usedLocales = useMemo( () => localizations.map( l => l.locale ), [ localizations ] );

    async function refresh() {
        const { data } = await fetchResource( { id: resourceId } );
        onChange?.( data.localizations );
    }

    return <Flex dir='horizontal' wrap className='translations-list'>
        { localizations.map( l =>
            <LocalizationCard key={ l.id } localization={ l } resourceId={ resourceId } type={ type }
                disabled={ disabled } excludedLocales={ usedLocales } onSaved={ refresh } />
        ) }
        <LocalizationCard isCreateButton resourceId={ resourceId } type={ type }
            disabled={ disabled } excludedLocales={ usedLocales } onSaved={ refresh } />
    </Flex>;
};

const LocalizationCard = ( { localization, resourceId, type, disabled, excludedLocales, onSaved, isCreateButton = false } ) => {

    const [ form ] = Form.useForm();
    const { create, patch, remove } = useResourceLocalizationMutations();

    const [ open, setOpen ] = useState( false );
    const [ loading, setLoading ] = useState( false );
    const [ error, setError ] = useState();

    // the card is clickable, so the delete action has to opt out of the card's hover treatment
    const [ deleteHovered, setDeleteHovered ] = useState( false );
    const [ deleteConfirming, setDeleteConfirming ] = useState( false );

    function close() {
        setOpen( false );
        setError();
    }

    function onClick() {
        if ( !open && !disabled ) {
            setOpen( true );
        }
    }

    async function handleOkClick() {
        setError();

        await form.validateFields()
            .then( async values => {
                setLoading( true );

                if ( isCreateButton ) {
                    await create( { resourceId, type, localization: values } );
                } else {
                    await patch( { resourceId, type, localization: values, existing: localization } );
                }

                await onSaved();
                close();
            } )
            .catch( e => {
                console.error( e );
                setError( <ExpandableAlert message='Could not save localization' error={ e } /> );
            } )
            .finally( () => {
                setLoading( false );
            } );
    }

    async function handleDeleteClick( e ) {

        e?.stopPropagation();

        setError();
        setLoading( true );

        try {
            await remove( { resourceId, localizationId: localization.id } );
            await onSaved();
            close();
        } catch ( e ) {
            console.error( e );
            setError( <ExpandableAlert message='Could not delete localization' error={ e } /> );
        } finally {
            setLoading( false );
        }
    }

    return <>
        { isCreateButton ?
            <Button
                type='dashed'
                disabled={ disabled }
                icon={ <PlusOutlined /> }
                onClick={ onClick }
                className='add-item-button'
            >
                Add Localization
            </Button> :
            <Card
                hoverable={ !deleteHovered && !deleteConfirming }
                onClick={ onClick }
                title={ <>{ localization.isDefault && <Tag color='green' >Default Localization</Tag> } <Tag color='blue'>{ localization.locale }</Tag></> }
                extra={ !localization.isDefault &&
                    <span
                        onClick={ e => e.stopPropagation() }
                        onMouseEnter={ () => setDeleteHovered( true ) }
                        onMouseLeave={ () => setDeleteHovered( false ) }
                    >
                        <Popconfirm title='Delete localization?' description='Are you sure you want to delete this localization?' onConfirm={ handleDeleteClick } onOpenChange={ setDeleteConfirming }><Button type='text' color='red' disabled={ disabled } icon={ <DeleteOutlined /> } /></Popconfirm>
                    </span>
                }
            >
                <Typography.Paragraph ellipsis={ { rows: 1 } }>
                    <strong>Name: </strong>{ localization.name }
                </Typography.Paragraph>

                <Typography.Paragraph ellipsis={ { rows: 2 } }>
                    <strong>Description: </strong>{ localization.description }
                </Typography.Paragraph>

                { type === RESOURCE_TYPE.URL ?
                    <Typography.Paragraph ellipsis={ { rows: 2 } }>
                        <strong>URL: </strong>
                        <a target='_blank' rel='noreferrer' href={ localization.url }><ColoredTag.Icon style={ { textOverflow: 'ellipsis', maxWidth: '100%', overflow: 'clip' } } value='URL'>{ localization.url.match( /https?:\/\/(.*)/ )?.[ 1 ] ?? localization.url }</ColoredTag.Icon></a>
                    </Typography.Paragraph> :
                    <Typography.Paragraph ellipsis={ { rows: 2 } }>
                        <strong>File: </strong>
                        <ColoredTag.Icon style={ { textOverflow: 'ellipsis', maxWidth: '100%', overflow: 'clip' } } value="File">{ localization.asset?.name }</ColoredTag.Icon>
                    </Typography.Paragraph>
                }
            </Card>
        }
        <Modal
            title={ isCreateButton ? 'Add Localization' : 'Edit Localization' }
            open={ open }
            closeIcon={ null }
            destroyOnHidden={ true }
            footer={ [
                ...( isCreateButton ? [] : [
                    <Popconfirm
                        key='delete'
                        title='Are you sure you want to delete this localization?'
                        onConfirm={ handleDeleteClick }
                        okText='Yes'
                        cancelText='No'>
                        <Button danger className='delete' disabled={ loading || localization.isDefault } icon={ <DeleteOutlined /> }>
                            Delete
                        </Button>
                    </Popconfirm>
                ] ),
                <Button key='cancel' onClick={ close } icon={ <CloseOutlined /> } disabled={ loading }>
                    Cancel
                </Button>,
                <Button key='save' color='primary' type='primary' onClick={ handleOkClick } loading={ loading }>
                    Save
                </Button>
            ] }
        >
            { /* the form instance outlives the modal, and on remount rc-field-form merges initialValues
                 *under* the surviving store - so without clearOnDestroy a reopened modal keeps showing
                 whatever was in the fields before the last save instead of the refetched localization */ }
            <Form form={ form } layout='vertical' disabled={ loading } initialValues={ localization } clearOnDestroy>
                <LocalizationFields
                    type={ type }
                    lockLocale={ !isCreateButton }
                    excludedLocales={ isCreateButton ? excludedLocales : excludedLocales?.filter( l => l !== localization.locale ) }
                />
            </Form>
            { localization?.isDefault &&
                <Typography.Text type='secondary'>The default localization can not be deleted.</Typography.Text> }
            { error && error }
        </Modal>
    </>;
};

const LocalizationFields = ( { namePrefix = [], type, excludedLocales, lockLocale, disabled, validateCb, dirtyCb } ) => {

    const { handleTextChange } = useTimedUserInput( { onTimeout: validateCb, onChange: dirtyCb } );

    return <>
        <Form.Item
            label='Language'
            name={ [ ...namePrefix, 'locale' ] }
            rules={ [ { required: true, message: 'Locale is required' } ] }
        >
            <LanguageSelectorCMX
                mode='select-single'
                disabled={ lockLocale || disabled }
                excludelocales={ excludedLocales }
                hideExcludedLocales={ false }
                onChange={ () => validateCb?.() }
            />
        </Form.Item>

        <Form.Item
            label='Name'
            name={ [ ...namePrefix, 'name' ] }
            rules={ [ { required: true, message: 'Name is required' } ] }
        >
            <Input disabled={ disabled } autoComplete='off' onChange={ handleTextChange } onBlur={ () => validateCb?.() } />
        </Form.Item>

        <Form.Item
            label='Description'
            name={ [ ...namePrefix, 'description' ] }
            rules={ [ { required: true, message: 'Description is required' } ] }
        >
            <Input.TextArea rows={ 4 } disabled={ disabled } onChange={ handleTextChange } onBlur={ () => validateCb?.() } />
        </Form.Item>

        <Form.Item
            label='URL'
            name={ [ ...namePrefix, 'url' ] }
            hidden={ type === RESOURCE_TYPE.FILE }
            rules={ [
                { 
                    required: type === RESOURCE_TYPE.URL, 
                    message: 'URL is required' 
                },
                { pattern: type !== RESOURCE_TYPE.URL ? undefined : /^http(s?):\/\//, message: 'Must include protocol ("http:" or "https:")' },
                {  validator: ( _, v ) => !( v?.length > 0 ) || type !== RESOURCE_TYPE.URL || isValidURL( v ) ? Promise.resolve() : Promise.reject( new Error( 'Invalid URL' ) ) },
            ] }
        >
            <Input disabled={ disabled } onChange={ handleTextChange } onBlur={ () => validateCb?.() } />
        </Form.Item>
        <Form.Item
            label='File'
            name={ [ ...namePrefix, 'asset' ] }
            hidden={ type === RESOURCE_TYPE.URL }
            rules={ [ 
                { 
                    required: type === RESOURCE_TYPE.FILE, 
                    message: 'File is required' 
                }
            ] }
        >
            <ResourceFileFormItem disabled={ disabled } onChange={ () => validateCb?.() } />
        </Form.Item>
    </>;
};

/**
 * Controlled upload for a localization's file. `file` is only present once the user picks a
 * new one, which is how the mutations know whether the asset needs re-uploading.
 */
const ResourceFileFormItem = ( { value, onChange, disabled } ) => {

    const fileList = useMemo( () => value ? [ {
        uid: value.name,
        name: value.name,
        status: 'done',
        url: value.url,
    } ] : [], [ value ] );

    return <Upload
        fileList={ fileList }
        maxCount={ 1 }
        listType='picture'
        disabled={ disabled }
        beforeUpload={ file => {
            onChange?.( { name: file.name, file } );
            return Upload.LIST_IGNORE;
        } }
        onRemove={ () => onChange?.( null ) }
    >
        { !value &&
            <Button type='primary' icon={ <UploadOutlined /> } disabled={ disabled }>
                Upload
            </Button>
        }
    </Upload>;
};

export default CreateEditResourcePage;
